import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '@/api/apiCall';
import { toast } from 'sonner';
import jeddhalogo from '@/assets/img/jed-watch.png';
import { useApiQueue } from '@/hooks/useApiQueue';
import { AssetsPanel } from '../../components/shared/AssetsPanel';
import LoadingComponent from '@/components/common/LoadingComponent';
import { API_CALL_TIME } from '@/constants/constant';

// ─── Fallback Data ────────────────────────────────────────────────────────────
const FALLBACK_PROFILE = {
  table_key: 'jed_watch_mkt_real_estate_profile',
  title: 'Jed Watch Mkt Real Estate - Profile',
  data: {
    project_name:    { label: 'Project Name', value: 'Jed Watch Mkt' },
    contract_term:   { label: 'Contract Term', value: '16 years and 10 month ;' },
    contract_period: { start_date: '02/01/2023', end_date: '11/11/2039' },
    operational_hours: { label: 'Operational Hours', value: '07:00 am - 12:00 am (17 Hours)' },
    vat_note:        { label: 'Rate per Hour', value: 'SAR 05 excluding 15% VAT' },
  },
};

const FALLBACK_FLOORS = {
  table_key: 'jed_watch_mkt_building_floors_breakdown',
  title: 'Jed Watch Mkt Building Floor Breakdown',
  data: {
    columns: [
      { column_key: 'building_floor', label: 'BUILDING FLOOR', type: 'text' },
      { column_key: 'floor_details', label: '05', type: 'group', sub_columns: [
        { column_key: 'floor_number', label: '', type: 'text' },
        { column_key: 'floor_type',   label: '', type: 'text' },
      ]},
      { column_key: 'qty', label: 'QTY', type: 'text' },
    ],
    rows: [
      { building_floor: '', floor_number: 'Ground Floor', floor_type: 'SHOPS',   qty: '76', building_rowSpan: 6 },
      { building_floor: '', floor_number: '1st FLOOR',    floor_type: 'PARKING', qty: '30 Spaces'},
      { building_floor: '', floor_number: '2nd FLOOR',    floor_type: 'PARKING', qty: '28 Spaces'},
      { building_floor: '', floor_number: '3rd FLOOR',    floor_type: 'PARKING', qty: '28 Spaces'},
      { building_floor: '', floor_number: '4th FLOOR',    floor_type: 'PARKING', qty: '30 Spaces'},
    ],
  },
};

const FALLBACK_MEMBERSHIP = {
  table_key: 'jed_watch_mkt_membership_tariff',
  title: 'Membership Tariff',
  data: {
    columns: ['MEMBERSHIP TARIFF', 'Price'],
    rows: [
      ['Causal Members',      'SAR 350 + VAT'],
      ['Shop Owners/Tenants', 'SAR 175 + VAT'],
    ],
  },
};

const FALLBACK_ASSETS = {
  table_key: 'jed_watch_mkt_assets_deployed',
  title: 'Jed Watch Mkt. Off-Street',
  data: {
    groups: [
      { group_name: 'Ticket Dispenser',    group_total_qty: '1',  items: [{ item_name: 'DIP G100', qty: '1' }, { item_name: 'G100 64 Bit', qty: '-' }, { item_name: 'G100 32 Bit', qty: '-' }] },
      { group_name: 'Ticket Verifier',     group_total_qty: '-',  items: [{ item_name: 'EV DIP G100', qty: '-' }, { item_name: 'EV G100 64 Bit', qty: '-' }, { item_name: 'EV G100 32 Bit', qty: '-' }] },
      { group_name: "APM's",               group_total_qty: '1',  items: [{ item_name: 'APM ETS-70', qty: '1' }, { item_name: 'APM ETS-100', qty: '-' }] },
      { group_name: 'PIN Pad',             group_total_qty: '1',  items: [{ item_name: 'IUP250', qty: '1' }] },
      { group_name: 'Card Reader',         group_total_qty: '1',  items: [{ item_name: 'IUR250', qty: '1' }] },
      { group_name: 'B-Contactless',       group_total_qty: '1',  items: [{ item_name: 'IUC150', qty: '1' }] },
      { group_name: 'RFID Reader',         group_total_qty: '3',  items: [] },
      { group_name: 'IP Barrier',          group_total_qty: '2',  items: [] },
      { group_name: 'ANPR Camera',         group_total_qty: '2',  items: [{ item_name: 'ARH', qty: '2' }, { item_name: 'Lumenera', qty: '-' }] },
      { group_name: 'Sliding Gate',        group_total_qty: '-',  items: [{ item_name: 'RIGHT', qty: '-' }, { item_name: 'LEFT', qty: '-' }] },
      { group_name: "PND's",               group_total_qty: '-',  items: [{ item_name: 'TPAL BNA-Large', qty: '-' }, { item_name: 'T-PAL TX', qty: '-' }, { item_name: 'T-PAL V2', qty: '-' }] },
      { group_name: 'Pound Cahier Device', group_total_qty: '-',  items: [{ item_name: 'CN-50', qty: '-' }] },
      { group_name: 'Driver Device',       group_total_qty: '-',  items: [{ item_name: 'CN-51', qty: '-' }] },
      { group_name: 'Enf Device',          group_total_qty: '-',  items: [{ item_name: 'Oukitel WP2', qty: '-' }, { item_name: 'Doogee V10', qty: '-' }] },
      { group_name: 'Validation',          group_total_qty: '-',  items: [{ item_name: 'Lenova Tablet', qty: '-' }] },
      { group_name: 'Printer',             group_total_qty: '-',  items: [{ item_name: 'PR-3', qty: '-' }, { item_name: 'SEWOO', qty: '-' }, { item_name: 'BIXOLON', qty: '-' }] },
      { group_name: 'Tow Truck',           group_total_qty: '-',  items: [] },
      { group_name: 'Clamp Vehicle',       group_total_qty: '-',  items: [] },
      { group_name: 'Support Vehicle',     group_total_qty: '-',  items: [] },
    ],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const groupsToFlat = (groups = []) => {
  const result = [];
  groups.forEach(g => {
    result.push({ name: g.group_name || '', isCategory: true, subCount: '', totalCount: String(g.group_total_qty ?? '-') });
    (g.items || []).forEach(item => {
      result.push({ name: item.item_name || '', isCategory: false, subCount: String(item.qty ?? '-'), totalCount: '' });
    });
  });
  return result;
};

const flatToGroups = (flat) => {
  const groups = [];
  let cur = null;
  flat.forEach(a => {
    if (a.isCategory) {
      if (cur) groups.push(cur);
      cur = { group_name: a.name, group_total_qty: a.totalCount || '', items: [] };
    } else if (cur) {
      cur.items.push({ item_name: a.name, qty: a.subCount || '' });
    }
  });
  if (cur) groups.push(cur);
  return groups;
};

// ─────────────────────────────────────────────────────────────────────────────
const JedWatch_4 = ({ year = '', monthNumber = '', contract = '' }) => {
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [floorsData, setFloorsData] = useState(null);
  const [membershipData, setMembershipData] = useState(null);
  const [assetsData, setAssetsData] = useState(null);
  
  const { enqueue } = useApiQueue();
  
  // Refs for profile inputs
  const projectNameRef = useRef(null);
  const contractTermRef = useRef(null);
  const contractStartDateRef = useRef(null);
  const contractEndDateRef = useRef(null);
  const operationalHoursRef = useRef(null);
  const vatNoteRef = useRef(null);
  
  // Refs for floors table
  const floorsCountLabelRef = useRef(null);
  const buildingFloorRefs = useRef([]);
  const floorNumberRefs = useRef([]);
  const floorTypeRefs = useRef([]);
  const floorQtyRefs = useRef([]);
  
  // Refs for membership table
  const membershipRefs = useRef([]);
  
  // Refs for assets
  const assetSubCountRefs = useRef([]);
  const assetTotalCountRefs = useRef([]);

  // State refs for data
  const profileDataRef = useRef(null);
  const floorsDataRef = useRef(null);
  const membershipDataRef = useRef(null);
  const assetsDataRef = useRef(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await enqueue(() =>
          apiCall('GET', '/boardpack/get-tables-data', { year, contract, month: monthNumber }));
        if (!response?.success) return;
        const data = response?.data || [];
        const find = (key, fb) => data.find(t => t.table_key === key) || fb;
        
        const profile = find('jed_watch_mkt_real_estate_profile', FALLBACK_PROFILE);
        const floors = find('jed_watch_mkt_building_floors_breakdown', FALLBACK_FLOORS);
        const membership = find('jed_watch_mkt_membership_tariff', FALLBACK_MEMBERSHIP);
        const assets = find('jed_watch_mkt_assets_deployed', FALLBACK_ASSETS);
        
        setProfileData(profile);
        setFloorsData(floors);
        setMembershipData(membership);
        setAssetsData(assets);
        
        profileDataRef.current = profile;
        floorsDataRef.current = floors;
        membershipDataRef.current = membership;
        assetsDataRef.current = assets;
      } catch (err) {
        console.error('Fetch error:', err);
      }
    };
    fetchData();
  }, [year, monthNumber, contract]);

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const autoSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      
      // Get current values from refs
      const currentProjectName = projectNameRef.current?.value || profileDataRef.current?.data?.project_name?.value || '';
      const currentContractTerm = contractTermRef.current?.value || profileDataRef.current?.data?.contract_term?.value || '';
      const currentStartDate = contractStartDateRef.current?.value || profileDataRef.current?.data?.contract_period?.start_date || '';
      const currentEndDate = contractEndDateRef.current?.value || profileDataRef.current?.data?.contract_period?.end_date || '';
      const currentOperationalHours = operationalHoursRef.current?.value || profileDataRef.current?.data?.operational_hours?.value || '';
      const currentVatNote = vatNoteRef.current?.value || profileDataRef.current?.data?.vat_note?.value || '';
      const currentFloorsCountLabel = floorsCountLabelRef.current?.value || floorsDataRef.current?.data?.columns?.find(c => c.type === 'group')?.label || '';
      
      // Build floors rows from refs
      const currentFloorRows = (floorsDataRef.current?.data?.rows || []).map((row, idx) => ({
        building_floor: buildingFloorRefs.current[idx]?.value || row.building_floor || '',
        floor_number: floorNumberRefs.current[idx]?.value || row.floor_number || '',
        floor_type: floorTypeRefs.current[idx]?.value || row.floor_type || '',
        qty: floorQtyRefs.current[idx]?.value || row.qty || '',
        building_rowSpan: row.building_rowSpan
      }));
      
      // Build membership rows from refs
      const currentMembershipRows = (membershipDataRef.current?.data?.rows || []).map((row, rowIdx) => 
        row.map((_, colIdx) => membershipRefs.current[rowIdx]?.[colIdx]?.value || row[colIdx] || '')
      );
      
      const response = await apiCall('POST', '/boardpack/add-tables-data', {
        contract, year, month: monthNumber, created_by: 1,
        tables: [
          { 
            table_key: profileDataRef.current?.table_key || 'jed_watch_mkt_real_estate_profile', 
            title: profileDataRef.current?.title || 'Jed Watch Mkt Real Estate - Profile', 
            data: {
              project_name: { label: 'Project Name', value: currentProjectName },
              contract_term: { label: 'Contract Term', value: currentContractTerm },
              contract_period: { start_date: currentStartDate, end_date: currentEndDate },
              operational_hours: { label: 'Operational Hours', value: currentOperationalHours },
              vat_note: { label: 'Rate per Hour', value: currentVatNote }
            }
          },
          { 
            table_key: floorsDataRef.current?.table_key || 'jed_watch_mkt_building_floors_breakdown', 
            title: floorsDataRef.current?.title || 'Jed Watch Mkt Building Floor Breakdown', 
            data: {
              columns: floorsDataRef.current?.data?.columns?.map(col => 
                col.type === 'group' ? { ...col, label: currentFloorsCountLabel } : col
              ),
              rows: currentFloorRows
            }
          },
          { 
            table_key: membershipDataRef.current?.table_key || 'jed_watch_mkt_membership_tariff', 
            title: membershipDataRef.current?.title || 'Membership Tariff', 
            data: {
              columns: membershipDataRef.current?.data?.columns || ['MEMBERSHIP TARIFF', 'Price'],
              rows: currentMembershipRows
            }
          },
          { 
            table_key: assetsDataRef.current?.table_key || 'jed_watch_mkt_assets_deployed', 
            title: assetsDataRef.current?.title || 'Jed Watch Mkt. Off-Street', 
            data: { groups: transformAssetsToApiFormat() }
          },
        ],
      });
      
      if (!response?.success) throw new Error(response?.message);
      // toast.success('Data saved successfully');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const transformAssetsToApiFormat = () => {
    const currentAssets = assetsDataRef.current?.data?.groups || [];
    const groups = [];
    let currentGroup = null;
    let flatIdx = 0;

    currentAssets.forEach((group, groupIdx) => {
      currentGroup = {
        group_name: group.group_name,
        group_total_qty: assetTotalCountRefs.current[flatIdx]?.value || group.group_total_qty || '-',
        items: []
      };
      flatIdx++;
      
      (group.items || []).forEach((item, itemIdx) => {
        currentGroup.items.push({
          item_name: item.item_name,
          qty: assetSubCountRefs.current[flatIdx]?.value || item.qty || '-'
        });
        flatIdx++;
      });
      
      groups.push(currentGroup);
    });
    
    return groups;
  };

  if (!profileData || !floorsData || !membershipData || !assetsData) {
    return <LoadingComponent/>;
  }

  const pd = profileData.data;
  const fd = floorsData.data;
  const floorRows = fd.rows || [];
  const memRows = membershipData.data.rows || [];
  const flatAssets = groupsToFlat(assetsData.data.groups);

  return (
    <div className="min-h-screen p-8 bg-white">

      {/* Header */}
      <div className="flex items-center mb-2">
        <img src={jeddhalogo} alt="Jeddha Logo" style={{ height: '50px', objectFit: 'contain' }} className="ml-2 mr-4" />
        <h1 className="text-xl font-bold flex-1 text-center">{profileData.title}</h1>
      </div>

      <div className="flex flex-row gap-0 !border-2 !border-black p-3">

        {/* ── Left ── */}
        <div className="flex-1 !border-black pr-4">
          <div className="text-[16px] font-semibold mb-4">Activities:</div>

          {/* Project Name */}
          <div className="flex items-center gap-2 mb-1 ml-4">
            <div className="w-4 h-4 !border-2 !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              {pd.project_name?.label}{' '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.project_name?.value || ''}
                  ref={projectNameRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px]"
                />
              </strong>
            </span>
          </div>

          {/* Contract Term */}
          <div className="flex items-center gap-2 mb-1 ml-10">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px] flex-wrap gap-1">
              Contract term{' '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.contract_term?.value || ''}
                  ref={contractTermRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-33" 
                />
              </strong>
              ; start date{' '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.contract_period?.start_date || ''}
                  ref={contractStartDateRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-18" 
                />
              </strong>
              {' to '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.contract_period?.end_date || ''}
                  ref={contractEndDateRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24" 
                />
              </strong>
            </span>
          </div>

          {/* Operational Hours */}
          <div className="flex items-center gap-2 mb-1 ml-10">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              Operational Hours –{' '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.operational_hours?.value || ''}
                  ref={operationalHoursRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-100" 
                />
              </strong>
            </span>
          </div>

          {/* Rate per Hour */}
          <div className="flex items-center gap-2 mb-1 ml-10">
            <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
            <span className="text-[12px]">
              Rate per Hour{' '}
              <strong>
                <input 
                  type="text" 
                  defaultValue={pd.vat_note?.value || ''}
                  ref={vatNoteRef}
                  onChange={autoSave}
                  className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px]" 
                />
              </strong>
            </span>
          </div>

          {/* Assets Deployed */}
          <div className="flex items-center gap-2 ml-10 mb-6">
            <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-[12px]">Assets Deployed</span>
          </div>

          {/* Building Floors Table */}
          <div className="ml-4 mb-6">
            <div className="inline-block border-2 border-black">
              <table className="!border-collapse text-[11px]">
                <thead>
                  <tr>
                    <th rowSpan={floorRows.length + 1} className="!border !border-black border-b-white! px-3 py-1 font-bold text-center bg-white align-middle" style={{ minWidth: '120px' }} />
                    <th colSpan={2} className="!border !border-black px-3 py-1 font-bold text-center bg-white" style={{ minWidth: '160px' }}>
                      <input
                        type="text"
                        defaultValue={fd.columns?.find(c => c.type === 'group')?.label || '05'}
                        ref={floorsCountLabelRef}
                        onChange={autoSave}
                        className="outline-none bg-transparent !border-0 text-center font-bold w-full"
                      />
                    </th>
                    <th className="!border !border-black px-3 py-1 font-bold text-center bg-white" style={{ minWidth: '150px' }}>QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {floorRows.map((row, idx) => (
                    <tr key={idx}>
                      {idx === 0 ? (
                        <td rowSpan={row.building_rowSpan || floorRows.length} className="!border !border-black px-2 py-1">
                          <div className="flex items-center justify-center h-full">
                            <input
                              type="text"
                              defaultValue={row.building_floor || ''}
                              ref={(el) => (buildingFloorRefs.current[idx] = el)}
                              onChange={autoSave}
                              className="outline-none bg-transparent !border-0 text-center text-[11px]"
                            />
                          </div>
                          <div className='text-center font-bold text-[12px]'>BUILDING FLOOR</div>
                        </td>
                      ) : null}
                      <td className="!border !border-black px-2 py-1 text-center">
                        <input
                          type="text"
                          defaultValue={row.floor_number || ''}
                          ref={(el) => (floorNumberRefs.current[idx] = el)}
                          onChange={autoSave}
                          className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
                        />
                      </td>
                      <td className="!border !border-black px-2 py-1 text-center">
                        <input
                          type="text"
                          defaultValue={row.floor_type || ''}
                          ref={(el) => (floorTypeRefs.current[idx] = el)}
                          onChange={autoSave}
                          className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
                        />
                      </td>
                      <td className="!border !border-black px-2 py-1 text-center">
                        <input
                          type="text"
                          defaultValue={row.qty || ''}
                          ref={(el) => (floorQtyRefs.current[idx] = el)}
                          onChange={autoSave}
                          className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Membership Tariff Table */}
          <div className="ml-4">
            <table className="!border-collapse text-[11px]">
              <thead>
                <tr>
                  <th colSpan={2} className="!border !border-black px-3 py-1.5 font-bold text-center">MEMBERSHIP TARIFF</th>
                </tr>
              </thead>
              <tbody>
                {memRows.map((row, rIdx) => (
                  <tr key={rIdx}>
                    {row.map((cell, cIdx) => {
                      if (!membershipRefs.current[rIdx]) membershipRefs.current[rIdx] = [];
                      return (
                        <td key={cIdx} className="!border !border-black px-3 py-1.5">
                          <input 
                            type="text" 
                            defaultValue={cell}
                            ref={(el) => (membershipRefs.current[rIdx][cIdx] = el)}
                            onChange={autoSave}
                            className="outline-none bg-transparent !border-0 w-full text-[11px] text-center" 
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Right — Assets ── */}
        <AssetsPanel
          assetsTable={{ title: assetsData.title }}
          flatAssets={flatAssets}
          subCountRefs={assetSubCountRefs}
          totalCountRefs={assetTotalCountRefs}
          onSubCount={(index, value) => {
            // Update assets data
            const updatedFlat = [...flatAssets];
            updatedFlat[index] = { ...updatedFlat[index], subCount: value };
            setAssetsData(prev => ({
              ...prev,
              data: { ...prev.data, groups: flatToGroups(updatedFlat) }
            }));
            assetsDataRef.current = {
              ...assetsDataRef.current,
              data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) }
            };
            autoSave();
          }}
          onTotalCount={(index, value) => {
            // Update assets data
            const updatedFlat = [...flatAssets];
            updatedFlat[index] = { ...updatedFlat[index], totalCount: value };
            setAssetsData(prev => ({
              ...prev,
              data: { ...prev.data, groups: flatToGroups(updatedFlat) }
            }));
            assetsDataRef.current = {
              ...assetsDataRef.current,
              data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) }
            };
            autoSave();
          }}
        />
      </div>

      {/* {isSaving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white text-xs px-3 py-1.5 rounded shadow-lg z-50">Saving...</div>
      )} */}
    </div>
  );
};

export default JedWatch_4;